import { useState } from 'react';
import { Plus, MoreHorizontal, MessageSquare, Check, Pencil, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/utils/cn';
import { useAgentStore } from '@/store/agent-store';
import { useConfirm } from '@/components/ui/confirm-dialog';
import type { SessionListItem } from '@/api/modules/sessions';

interface SessionSidebarProps {
  activeSessionId: string | null;
  onSelect: (sessionId: string) => void;
  onCreate: () => void;
}

/**
 * 项目内会话列表侧栏（二级栏）。
 * 展示当前项目下的会话，支持切换 / 新建 / 重命名 / 删除。
 * 支持折叠/展开，折叠后悬停可临时展开。
 */
export function SessionSidebar({ activeSessionId, onSelect, onCreate }: SessionSidebarProps): JSX.Element {
  const sessionList = useAgentStore((s) => s.sessionList);
  const renameSession = useAgentStore((s) => s.renameSession);
  const deleteSession = useAgentStore((s) => s.deleteSession);
  const { confirm, dialog } = useConfirm();

  const [collapsed, setCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem('playgen.sessionSidebarCollapsed');
      return saved === '1'; // '1' = 折叠，'0' = 展开，默认展开
    } catch {
      return false;
    }
  });
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string; title: string } | null>(null);

  const saveRename = async () => {
    if (!editing) return;
    const title = editing.title.trim();
    if (title) await renameSession(editing.id, title).catch(() => {});
    setEditing(null);
  };

  const renderItem = (session: SessionListItem) => {
    const isActive = session.id === activeSessionId;
    const isEditing = editing?.id === session.id;
    const displayTitle = session.title || '未命名会话';

    // 相对时间
    const timeAgo = (() => {
      const diff = Date.now() - new Date(session.createdAt).getTime();
      const mins = Math.floor(diff / 60000);
      if (mins < 1) return '刚刚';
      if (mins < 60) return `${mins}分钟前`;
      const hours = Math.floor(mins / 60);
      if (hours < 24) return `${hours}小时前`;
      const days = Math.floor(hours / 24);
      if (days < 7) return `${days}天前`;
      return new Date(session.createdAt).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' });
    })();

    return (
      <div
        key={session.id}
        onClick={() => !isEditing && onSelect(session.id)}
        className={cn(
          // 用户要求（2026-08-26）：标题与时间同一行，时间是次要信息靠右缩小
          'group relative flex items-center gap-1.5 rounded-xl px-3 py-2.5 text-sm transition-all cursor-pointer border',
          isActive
            ? 'border-neon-cyan/30 bg-primary/10 text-foreground shadow-sm shadow-neon-cyan/5'
            : 'border-transparent text-foreground/80 hover:bg-muted/60 hover:border-border/40',
        )}
      >
        {isEditing ? (
          <input
            autoFocus
            className="input-neon min-w-0 flex-1 px-1.5 py-0.5 text-xs"
            value={editing.title}
            onChange={(e) => setEditing({ id: editing.id, title: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter') saveRename(); if (e.key === 'Escape') setEditing(null); }}
            onClick={(e) => e.stopPropagation()}
            onBlur={saveRename}
          />
        ) : (
          <>
            <span className="min-w-0 flex-1 truncate text-xs font-medium leading-tight">{displayTitle}</span>
            {isActive && <span className="h-1 w-1 shrink-0 rounded-full bg-neon-cyan" />}
            <span className="shrink-0 text-[10px] text-muted-foreground/60">{timeAgo}</span>
          </>
        )}

        {isEditing ? (
          <button onClick={(e) => { e.stopPropagation(); saveRename(); }} className="icon-btn shrink-0 rounded p-0.5"><Check size={12} /></button>
        ) : (
          <button
            onClick={(e) => { e.stopPropagation(); setMenuFor(menuFor === session.id ? null : session.id); }}
            className={cn('shrink-0 rounded p-0.5 transition-opacity', isActive ? 'opacity-60' : 'opacity-0 group-hover:opacity-60')}
          >
            <MoreHorizontal size={12} />
          </button>
        )}

        {menuFor === session.id && !isEditing && (
          <div
            className="absolute right-2 top-full z-20 mt-1 min-w-28 rounded-lg border border-border/50 bg-card p-1 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => { setEditing({ id: session.id, title: session.title ?? '' }); setMenuFor(null); }}
              className="flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-left text-xs text-foreground hover:bg-muted/50"
            >
              <Pencil size={12} />重命名
            </button>
            <button
              onClick={() => {
                setMenuFor(null);
                confirm('删除会话', '确定要删除这个会话吗？删除后无法恢复。', () => { deleteSession(session.id).catch((e: Error) => toast.error(e?.message || '删除会话失败')); });
              }}
              className="flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-left text-xs text-red-400 hover:bg-red-500/10"
            >
              <Trash2 size={12} />删除
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={cn(
        'flex h-full flex-col transition-all duration-300',
        collapsed ? 'w-12' : 'w-52'
      )}
      onClick={() => menuFor && setMenuFor(null)}
    >
      {/* 展开状态：完整内容 */}
      {!collapsed && (
        <div className="flex h-full flex-col overflow-hidden">
          <div className="flex shrink-0 items-center justify-between border-b border-border/60 px-2 py-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-foreground/90">会话</span>
            <div className="flex items-center gap-1">
              <button onClick={onCreate} className="icon-btn rounded p-1" title="新建会话"><Plus size={14} /></button>
              <button
                onClick={() => {
                  setCollapsed(true);
                  try { localStorage.setItem('playgen.sessionSidebarCollapsed', '1'); } catch { /* ignore */ }
                }}
                className="icon-btn rounded p-1 transition-colors hover:text-primary"
                title="折叠会话列表"
              >
                <ChevronLeft size={14} />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-1.5">
            {sessionList.length === 0 ? (
              <div className="py-8 text-center text-sm text-foreground/70">暂无会话</div>
            ) : (
              <div className="flex flex-col gap-0.5">{sessionList.map(renderItem)}</div>
            )}
          </div>
        </div>
      )}

      {/* 折叠状态：只显示当前会话图标 */}
      {collapsed && (
        <div className="flex h-full flex-col items-center overflow-hidden">
          <div className="flex w-full shrink-0 items-center justify-center border-b border-border/60 py-2">
            <button
              onClick={() => {
                setCollapsed(false);
                try { localStorage.setItem('playgen.sessionSidebarCollapsed', '0'); } catch { /* ignore */ }
              }}
              className="icon-btn rounded p-1 transition-colors hover:text-primary"
              title="展开会话列表"
            >
              <ChevronRight size={14} />
            </button>
          </div>
          <div className="flex flex-1 flex-col items-center justify-start gap-3 pt-4">
            {/* 新建会话按钮 */}
            <button
              onClick={onCreate}
              className="rounded-lg p-2 text-foreground/60 transition-colors hover:bg-primary/15 hover:text-primary"
              title="新建会话"
            >
              <Plus size={16} />
            </button>
            {/* 当前会话图标 */}
            {activeSessionId && (
              <div className="rounded-lg bg-primary/15 p-2 text-primary">
                <MessageSquare size={16} />
              </div>
            )}
          </div>
        </div>
      )}

      {dialog}
    </div>
  );
}
